import { AutoT } from "@/i18n/AutoT";
import { useAutoTranslate, useAutoTranslator } from "@/i18n/useAutoTranslate";
import { LangSwitchPill } from "@/components/LangSwitchPill";
import { useObT } from "@/lib/onboarding-i18n";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, useCallback, createContext, useContext } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { VersionHistoryDialog } from "@/components/onboarding/VersionHistoryDialog";
import { Loader2, CheckCircle2, Upload, X, Clock, Sparkles, Lock, ChevronLeft, ChevronRight, Star, MapPin, Navigation, Info, Moon } from "lucide-react";
import { compressImage, IMAGE_ACCEPT_ATTR } from "@/lib/compress-image";
import { MAX_ONBOARDING_PHOTOS, MAX_PHOTOS_MESSAGE } from "@/lib/onboarding-limits";
import {
  activatePartnerFromToken,
  deferPartnerOnboardingByToken,
  getPartnerOnboardingByToken,
  reopenOwnOnboardingByToken,
  savePartnerOnboardingByToken,
  uploadPartnerOnboardingMenuFile,
  submitPartnerOnboardingByToken,
} from "@/lib/partner.functions";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePublicCities } from "@/hooks/use-public-cities";

function useCategoryOptions() {
  const cats = useQuery({
    queryKey: ["onboard", "categories-all"],
    queryFn: async () => {
      const { data } = await supabase
        .from("categories")
        .select("label")
        .eq("active", true)
        .order("sort_order");
      return Array.from(new Set((data ?? []).map((r: any) => (r.label as string)).filter(Boolean)));
    },
    staleTime: 5 * 60_000,
  });
  const subs = useQuery({
    queryKey: ["onboard", "subcategories-all"],
    queryFn: async () => {
      const { data } = await supabase
        .from("places")
        .select("subcategory")
        .not("subcategory", "is", null)
        .limit(1000);
      return Array.from(
        new Set((data ?? []).map((r: any) => (r.subcategory as string)?.trim()).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b));
    },
    staleTime: 5 * 60_000,
  });
  // Subcategories are intentionally NOT scoped to the chosen main category — a
  // burger restaurant can also be a cocktail bar. Category labels are merged in
  // so every venue type is pickable as a subcategory too.
  const subcategories = Array.from(
    new Set([...(subs.data ?? []), ...(cats.data ?? [])].map((s) => s.trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));
  return { categories: cats.data ?? [], subcategories };
}

function SelectOrOther({
  value, onChange, options, placeholder, className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  className: string;
}) {
  const obt = useObT();
  const known = options.includes(value);
  const [mode, setMode] = useState<"select" | "other">(value && !known ? "other" : "select");
  useEffect(() => {
    if (value && !options.includes(value)) setMode("other");
  }, [value, options]);
  if (mode === "other") {
    return (
      <div className="flex gap-2">
        <input
          className={className}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
        />
        <button
          type="button"
          onClick={() => { onChange(""); setMode("select"); }}
          className="text-xs text-slate-500 hover:text-slate-700 underline shrink-0"
        >
          list
        </button>
      </div>
    );
  }
  return (
    <select
      className={className}
      value={value || ""}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "__other__") { setMode("other"); onChange(""); }
        else onChange(v);
      }}
    >
      <option value="">{placeholder || "Select…"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
      <option value="__other__">{obt("Other (type your own)\u2026")}</option>
    </select>
  );
}

/**
 * Comma-separated tags. Keeps the raw typed text in local state so typing a
 * comma (or trailing spaces) isn't swallowed by the parse/join round-trip —
 * the previous version made it impossible to add a second tag.
 */
function TagsInput({
  tags, onChange, className,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  className: string;
}) {
  const obt = useObT();
  const [text, setText] = useState(tags.join(", "));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(tags.join(", "));
  }, [tags, focused]);
  const parse = (v: string) =>
    Array.from(new Set(v.split(",").map((t) => t.trim()).filter(Boolean)));
  return (
    <input
      className={className}
      value={text}
      placeholder={obt("brunch, terrace, live music")}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parse(e.target.value));
      }}
      onBlur={(e) => {
        setFocused(false);
        const parsed = parse(e.target.value);
        onChange(parsed);
        setText(parsed.join(", "));
      }}
    />
  );
}


export const Route = createFileRoute("/onboard/$token")({
  head: () => ({
    meta: [
      { title: "Partner Onboarding — What Finland" },
      { name: "description", content: "Complete your partner onboarding to get listed on What Finland." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OnboardPage,
});

const MAX_BYTES = 100 * 1024;
const HUGE_FILE_BYTES = 100 * 1024 * 1024; // 100 MB
const HUGE_TARGET_BYTES = 100 * 1024; // compress giant uploads down to ~100 KB
// Pick a per-file target: giant uploads get squeezed harder so submission still succeeds.
function targetBytesFor(file: File): number {
  return file.size > HUGE_FILE_BYTES ? HUGE_TARGET_BYTES : MAX_BYTES;
}
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

type Hours = Record<(typeof DAYS)[number], { closed: boolean; open: string; close: string }>;
type BusinessType = "restaurant" | "place" | "ticket";
type StoredImage = { name: string; dataUrl: string; size: number };
type LocationEntry = {
  id: string;
  label: string;
  address: string;
  city: string;
  lat?: number | null;
  lng?: number | null;
  hours?: Hours | null; // null / undefined = inherit primary
  images?: StoredImage[];
};
type DraftDish = {
  section?: string;
  name: string;
  description?: string;
  price?: string;
  diet?: string;
};

type Draft = {
  business_type?: BusinessType;
  name?: string;
  address?: string;
  postal_code?: string;
  region?: "lapland" | "lakeland" | "archipelago" | "coast" | null;
  city?: string;
  website?: string;
  instagram?: string;
  facebook?: string;
  description?: string;
  category?: string;
  vibe?: string;
  hours?: Hours;
  // Extended fields (parity with admin place editor)
  phone?: string;
  email?: string;
  area?: string;
  subcategory?: string;
  price?: string;
  tags?: string[];
  overview_body?: string;
  details_body?: string;
  tickets_info?: string;
  schedule_info?: string;
  buy_ticket_url?: string;
  cta?: string;
  lat?: number | null;
  lng?: number | null;
  // Menu (restaurants): link to an existing menu page, uploaded PDF, uploaded photo
  menu_external_url?: string;
  menu_pdf_url?: string;
  menu_image_url?: string;
  menu_items?: DraftDish[];
  // Finland-wide topic hubs this business belongs to (slugs, e.g. "saunas").
  // References only — the listing itself is never duplicated per hub.
  hubs?: string[];
  // Additional business locations (primary lives on the top-level fields above)
  locations?: LocationEntry[];
};


const HUB_OPTIONS: { slug: string; label: string }[] = [
  { slug: "restaurants", label: "Restaurants" },
  { slug: "cafes", label: "Cafés" },
  { slug: "bars", label: "Bars" },
  { slug: "pubs", label: "Pubs" },
  { slug: "saunas", label: "Saunas" },
  { slug: "ski-resorts", label: "Ski resorts" },
  { slug: "national-parks", label: "National parks" },
];

const emptyHours: Hours = DAYS.reduce((acc, d) => {
  acc[d] = { closed: false, open: "10:00", close: "18:00" };
  return acc;
}, {} as Hours);

function makeLocationId() {
  return `loc_${Math.random().toString(36).slice(2, 10)}`;
}

function emptyLocation(): LocationEntry {
  return { id: makeLocationId(), label: "", address: "", city: "", lat: null, lng: null, hours: null, images: [] };
}


function fileToDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
}

function OnboardPage() {
  const obt = useObT();
  const { token } = Route.useParams();
  const [phase, setPhase] = useState<"loading" | "landing" | "form" | "done" | "invalid" | "expired">("loading");
  const [clientName, setClientName] = useState<string>("");
  const [allowedLocations, setAllowedLocations] = useState<number>(1);
  const [capDiagnostic, setCapDiagnostic] = useState<{ mismatch: boolean; rpc_cap: number; contract_max: number; expected_cap: number; contracts_seen: number; latest_contract_allowed?: number | null } | null>(null);
  const [draft, setDraft] = useState<Draft>({ hours: emptyHours });
  const { categories: categoryOptions, subcategories: subcategoryOptions } = useCategoryOptions();
  const [images, setImages] = useState<StoredImage[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0); // 0-100
  const [uploadingCount, setUploadingCount] = useState(0);
  const [sizeNotes, setSizeNotes] = useState<{ name: string; before: number; after: number }[]>([]);

  const [savingState, setSavingState] = useState<"idle" | "saving" | "saved">("idle");
  const [submitting, setSubmitting] = useState(false);
  const tx = useAutoTranslator();
  const [password, setPassword] = useState("");
  const [portalEmail, setPortalEmail] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [locating, setLocating] = useState(false);

  function pickCurrentLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      toast.error(obt("Your browser doesn't support location services."));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        setDraft((d) => ({ ...d, lat, lng }));
        setLocating(false);
        toast.success(obt("Location captured. Please confirm the address matches."));
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? obt("Location permission denied. Enable it in your browser settings, or type coordinates manually.")
            : obt("Couldn't read your location. Try again or type coordinates manually.");
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }

  const activate = useServerFn(activatePartnerFromToken);
  const getOnboarding = useServerFn(getPartnerOnboardingByToken);
  const saveOnboarding = useServerFn(savePartnerOnboardingByToken);
  const submitOnboarding = useServerFn(submitPartnerOnboardingByToken);
  const deferOnboarding = useServerFn(deferPartnerOnboardingByToken);
  const reopenOwn = useServerFn(reopenOwnOnboardingByToken);
  const uploadMenuFile = useServerFn(uploadPartnerOnboardingMenuFile);
  const [menuUploading, setMenuUploading] = useState<"pdf" | "image" | null>(null);
  const [reopening, setReopening] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPayload = useRef<string>("");

  const lsKey = `wf:onboard:${token}`;
  const lsImgKey = `wf:onboard:${token}:images`;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // Load session (merge server data with any locally cached draft so a refresh
  // never loses what the user just typed)
  useEffect(() => {
    (async () => {
      let localDraft: Draft | null = null;
      let localImages: StoredImage[] | null = null;
      try {
        const raw = typeof window !== "undefined" ? window.localStorage.getItem(lsKey) : null;
        if (raw) {
          const parsed = JSON.parse(raw) as { draft?: Draft; images?: StoredImage[] };
          localDraft = parsed.draft ?? null;
          localImages = Array.isArray(parsed.images) ? parsed.images : null;
        }
        // Photos are cached under a separate key so a storage-quota failure on
        // the (large) images never wipes the small text draft.
        const rawImg = typeof window !== "undefined" ? window.localStorage.getItem(lsImgKey) : null;
        if (rawImg) {
          const parsedImg = JSON.parse(rawImg) as StoredImage[];
          if (Array.isArray(parsedImg) && parsedImg.length) localImages = parsedImg;
        }
      } catch { /* ignore */ }


      const data = await getOnboarding({ data: { token } });
      if (!data) { setPhase("invalid"); return; }
      const d = data as { ok: boolean; error?: string; status?: string; draft?: Draft; images?: StoredImage[]; client_name?: string; client_info?: { name?: string; address?: string; city?: string; website?: string }; allowed_locations?: number; cap_diagnostic?: { mismatch: boolean; rpc_cap: number; contract_max: number; expected_cap: number; contracts_seen: number; latest_contract_allowed?: number | null } };
      if (!d.ok) { setPhase(d.error === "expired" ? "expired" : "invalid"); return; }
      setClientName(d.client_name || "");
      setAllowedLocations(Math.max(1, Number(d.allowed_locations ?? 1)));
      if (d.cap_diagnostic?.mismatch) {
        setCapDiagnostic(d.cap_diagnostic);
        console.warn("[onboarding.cap.mismatch]", d.cap_diagnostic);
      } else {
        setCapDiagnostic(null);
      }
      const ci = d.client_info || {};
      const mergedDraft: Draft = {
        ...(d.draft || {}),
        ...(localDraft || {}),
        // Prefill business info from the CRM record so partners don't re-enter details we already have.
        name: (localDraft?.name ?? d.draft?.name) || ci.name || "",
        address: (localDraft?.address ?? d.draft?.address) || ci.address || "",
        city: (localDraft?.city ?? d.draft?.city) || ci.city || "",
        website: (localDraft?.website ?? d.draft?.website) || ci.website || "",
        hours: { ...emptyHours, ...(d.draft?.hours || {}), ...(localDraft?.hours || {}) },
      };
      const mergedImages = localImages && localImages.length ? localImages : (Array.isArray(d.images) ? d.images : []);
      setDraft(mergedDraft);
      setImages(mergedImages);
      setPhase(d.status === "submitted" ? "done" : "landing");
      lastPayload.current = JSON.stringify({ draft: d.draft || {}, images: d.images || [] });
    })();
  }, [getOnboarding, token, lsKey]);

  // Persist to localStorage on every change so a refresh restores state instantly.
  // Text draft and photos are written separately: photos can blow the storage
  // quota, and when they do the text must still survive.
  useEffect(() => {
    if (phase === "loading" || phase === "invalid" || phase === "expired") return;
    try {
      window.localStorage.setItem(lsKey, JSON.stringify({ draft }));
    } catch { /* quota — ignore */ }
  }, [draft, phase, lsKey]);

  useEffect(() => {
    if (phase === "loading" || phase === "invalid" || phase === "expired") return;
    try {
      window.localStorage.setItem(lsImgKey, JSON.stringify(images));
    } catch { /* quota — photos live on the server draft instead */ }
  }, [images, phase, lsImgKey]);

  // Autosave (debounced)
  const scheduleSave = useCallback((nextDraft: Draft, nextImages: StoredImage[]) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = JSON.stringify({ draft: nextDraft, images: nextImages });
      if (payload === lastPayload.current) { setDirty(false); return; }
      setSavingState("saving");
      try {
        const data = await saveOnboarding({ data: { token, draft: nextDraft as Record<string, unknown>, images: nextImages as unknown as Record<string, unknown>[] } });
        const d = data as { ok?: boolean; field?: string; message?: string; name?: string; warnings?: { field?: string; errors?: Record<string, string> } } | null;
        if (d?.ok) {
          lastPayload.current = payload;
          setSavingState("saved");
          setSaveError(null);
          setDirty(false);
          setTimeout(() => setSavingState("idle"), 1200);
          if (d.warnings?.errors) {
            setServerFieldErrors((prev) => ({ ...prev, ...(d.warnings!.errors as Record<string, string>) }));
          }
        }
        else {
          setSavingState("idle");
          if (d && d.ok === false && d.field === "images" && d.message) {
            setImageError(d.message);
            setSaveError(d.message);
            toast.error(d.message);
          } else if (d && d.ok === false && d.field === "city" && d.message) {
            setServerFieldErrors((prev) => ({ ...prev, city: d.message! }));
            setSaveError(d.message);
            toast.error(d.message);
          } else {
            setSaveError(obt("Your latest changes are not saved yet. Press Save to try again."));
          }
        }
      } catch {
        setSavingState("idle");
        setSaveError("Couldn't reach the server — your changes are kept in this browser. Press Save to retry.");
      }
    }, 700);
  }, [saveOnboarding, token]);

  useEffect(() => {
    if (phase !== "form") return;
    if (JSON.stringify({ draft, images }) !== lastPayload.current) setDirty(true);
    scheduleSave(draft, images);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [draft, images, phase, scheduleSave]);

  // Keep the newest state in a ref so background savers (retry timer, tab-hide
  // flush) always push the latest data without re-registering listeners.
  const latest = useRef<{ draft: Draft; images: StoredImage[] }>({ draft, images });
  useEffect(() => { latest.current = { draft, images }; }, [draft, images]);

  // Background retry: if a save failed (offline, hiccup), keep trying so the
  // server copy catches up even when the user never touches the form again.
  useEffect(() => {
    if (phase !== "form") return;
    const id = setInterval(() => {
      const payload = JSON.stringify(latest.current);
      if (payload === lastPayload.current) return;
      scheduleSave(latest.current.draft, latest.current.images);
    }, 15_000);
    return () => clearInterval(id);
  }, [phase, scheduleSave]);

  // Flush to the server the moment the tab is hidden or closed, so nothing
  // depends on localStorage surviving.
  useEffect(() => {
    if (phase !== "form") return;
    const flush = () => {
      const payload = JSON.stringify(latest.current);
      if (payload === lastPayload.current) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      const snapshot = latest.current;
      void saveOnboarding({
        data: {
          token,
          draft: snapshot.draft as Record<string, unknown>,
          images: snapshot.images as unknown as Record<string, unknown>[],
        },
      })
        .then((res) => {
          const d = res as { ok?: boolean } | null;
          if (d?.ok) lastPayload.current = payload;
        })
        .catch(() => { /* retry timer / manual save will handle it */ });
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") flush(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", flush);
    };
  }, [phase, saveOnboarding, token]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (phase !== "form" || (!dirty && !saveError)) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase, dirty, saveError]);



  // Clear server-side field errors as the user edits the form
  useEffect(() => {
    if (Object.keys(serverFieldErrors).length === 0) return;
    setServerFieldErrors({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, images]);


  const applyRestoredVersion = useCallback((nextDraft: Record<string, unknown>, nextImages: Array<Record<string, unknown>>) => {
    const d = { ...(nextDraft as Draft) };
    d.hours = { ...emptyHours, ...(d.hours || {}) };
    const imgs = (nextImages as unknown as StoredImage[]) ?? [];
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setDraft(d);
    setImages(imgs);
    lastPayload.current = JSON.stringify({ draft: d, images: imgs });
    setDirty(false);
    setSaveError(null);
    try {
      window.localStorage.setItem(lsKey, JSON.stringify({ draft: d }));
      window.localStorage.setItem(lsImgKey, JSON.stringify(imgs));
    } catch { /* quota — server copy is authoritative */ }
  }, [lsKey, lsImgKey]);

  async function manualSave() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSavingState("saving");
    try {
      const data = await saveOnboarding({ data: { token, draft: draft as Record<string, unknown>, images: images as unknown as Record<string, unknown>[] } });
      const d = data as { ok?: boolean; field?: string; message?: string } | null;
      if (!d?.ok) {
        const msg = d?.field === "images" && d.message ? d.message : obt("Couldn't save draft");
        if (d?.field === "images" && d.message) setImageError(d.message);
        setSaveError(msg);
        toast.error(msg);
        setSavingState("idle");
      }
      else {
        lastPayload.current = JSON.stringify({ draft, images });
        setSavingState("saved");
        setImageError(null);
        setSaveError(null);
        setDirty(false);
        toast.success(obt("Draft saved \u2014 you can close this page and come back later"));
        setTimeout(() => setSavingState("idle"), 1400);
      }
    } catch {
      setSavingState("idle");
      setSaveError("Couldn't reach the server — your changes are kept in this browser. Press Save to retry.");
      toast.error(obt("Couldn't save right now. Your changes are still here \u2014 try again."));
    }
  }



  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    const ALLOWED = ["image/jpeg","image/jpg","image/png","image/webp","image/avif"];
    let arr = Array.from(files);
    // Enforce max photo cap up front so partners get a clear message
    // instead of a silent server rejection.
    const remaining = Math.max(0, MAX_ONBOARDING_PHOTOS - images.length);
    if (remaining <= 0) {
      setImageError(MAX_PHOTOS_MESSAGE);
      toast.error(MAX_PHOTOS_MESSAGE);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    let dropped = 0;
    if (arr.length > remaining) {
      dropped = arr.length - remaining;
      arr = arr.slice(0, remaining);
    }
    setUploadingCount(arr.length);
    setUploading(1);
    setImageError(null);
    const out: StoredImage[] = [];
    const notes: { name: string; before: number; after: number }[] = [];
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      if (!ALLOWED.includes(f.type.toLowerCase())) {
        const msg = `${f.name}: unsupported file type. Use JPG, PNG, WebP, or AVIF.`;
        setImageError(msg); toast.error(msg);
        setUploading(Math.round(((i + 1) / arr.length) * 100));
        continue;
      }
      try {
        const before = f.size;
        const target = targetBytesFor(f);
        const compressed = await compressImage(f, target);
        if (compressed.size > target) throw new Error(`still larger than ${Math.round(target / 1024)} KB after compression`);
        const dataUrl = await fileToDataUrl(compressed);
        out.push({ name: f.name, dataUrl, size: compressed.size });
        notes.push({ name: f.name, before, after: compressed.size });
      } catch (e) {
        const msg = `${f.name}: ${(e as Error).message || "compress failed"}`;
        setImageError(msg); toast.error(msg);
      }
      setUploading(Math.round(((i + 1) / arr.length) * 100));
    }
    setImages((prev) => [...prev, ...out]);
    setSizeNotes(notes);
    setUploading(0);
    setUploadingCount(0);
    if (dropped > 0) {
      setImageError(MAX_PHOTOS_MESSAGE);
      toast.error(MAX_PHOTOS_MESSAGE);
    }
    if (fileRef.current) fileRef.current.value = "";
  }



  function removeImage(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveImage(from: number, to: number) {
    setImages((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = prev.slice();
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  }

  function validateHours(): Partial<Record<(typeof DAYS)[number], string>> {
    const errs: Partial<Record<(typeof DAYS)[number], string>> = {};
    for (const d of DAYS) {
      const h = draft.hours?.[d];
      if (!h) { errs[d] = obt("Set hours or mark as Closed."); continue; }
      if (h.closed) continue;
      if (!h.open || !h.close) { errs[d] = obt("Enter opening and closing times."); continue; }
      // Closing after midnight (e.g. 17:00 → 00:00 / 02:00) is valid — it rolls over
      // to the next day. Only identical open/close times are ambiguous.
      if (h.open === h.close) { errs[d] = obt("Opening and closing times cannot be the same."); continue; }
    }
    return errs;
  }
  const hoursErrorsLocal = validateHours();
  const hoursErrors: Partial<Record<(typeof DAYS)[number], string>> = { ...hoursErrorsLocal };
  for (const d of DAYS) {
    const s = serverFieldErrors[`hours.${d}`];
    if (s) hoursErrors[d] = s;
  }

  const businessType = draft.business_type;
  const needsHours = businessType === "restaurant" || businessType === "place";
  const isTicket = businessType === "ticket";

  const fieldErrors = {
    business_type: (!businessType ? obt("Choose what you're listing.") : null) || serverFieldErrors.business_type || null,
    name: (!draft.name?.trim() ? obt("Business name is required.") : null) || serverFieldErrors.name || null,
    address: (!draft.address?.trim() ? obt("Address is required.") : null) || serverFieldErrors.address || null,
    city: (!draft.city?.trim() ? obt("City is required.") : null) || serverFieldErrors.city || null,
    description: (!draft.description?.trim() ? obt("Description is required.") : null) || serverFieldErrors.description || null,
    category: (!draft.category?.trim() ? obt("Category is required.") : null) || serverFieldErrors.category || null,
    subcategory: (!draft.subcategory?.trim() ? obt("Subcategory is required.") : null) || serverFieldErrors.subcategory || null,
  };
  const hoursGeneric = serverFieldErrors.hours || null;
  const hasAnyOpen = DAYS.some((d) => !draft.hours?.[d]?.closed);
  const passwordError = password.length > 0 && password.length < 8 ? obt("At least 8 characters.") : null;
  const isFormInvalid =
    Object.values(fieldErrors).some(Boolean) ||
    (needsHours && (Object.keys(hoursErrors).length > 0 || !hasAnyOpen));



  function validate(): string | null {
    if (fieldErrors.business_type) return fieldErrors.business_type;
    if (fieldErrors.name) return fieldErrors.name;
    if (fieldErrors.address) return fieldErrors.address;
    if (fieldErrors.city) return fieldErrors.city;
    if (fieldErrors.description) return fieldErrors.description;
    if (fieldErrors.category) return fieldErrors.category;
    if (fieldErrors.subcategory) return fieldErrors.subcategory;
    if (needsHours && Object.keys(hoursErrors).length > 0) return obt("Complete opening hours for every day (or mark days as Closed).");
    if (needsHours && !hasAnyOpen) return "At least one day must have opening hours.";
    return null;
  }


  async function submit() {
    setGenericError(null);
    setRetryable(false);
    if (uploading > 0) { toast.error(obt("Please wait for image uploads to finish.")); return; }
    if (images.length < 1) {
      const msg = obt("At least 1 photo is required to submit.");
      setImageError(msg);
      setServerFieldErrors({ images: msg });
      setGenericError(msg);
      setShowErrorSummary(true);
      toast.error(msg);
      requestAnimationFrame(() => {
        const el = document.getElementById("onboard-error-summary");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        (el as HTMLElement | null)?.focus({ preventScroll: true });
      });
      return;
    }
    const err = validate();
    if (err) {
      setShowErrorSummary(true);
      setServerFieldErrors({});
      toast.error(err);
      requestAnimationFrame(() => {
        const el = document.getElementById("onboard-error-summary");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
        (el as HTMLElement | null)?.focus({ preventScroll: true });
      });
      return;
    }
    setShowErrorSummary(false);


    setSubmitting(true);
    // Auto-retry on transient network / server failures. Draft + images
    // remain in state (and localStorage) until success, so retries never
    // lose entered information.
    const MAX_ATTEMPTS = 3;
    const backoffs = [600, 1600, 3500];
    let data: unknown = null;
    let lastThrown: unknown = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      setRetryAttempt(attempt);
      try {
        data = await submitOnboarding({ data: { token, draft: draft as Record<string, unknown>, images: images as unknown as Record<string, unknown>[] } });
        lastThrown = null;
        break;
      } catch (e) {
        lastThrown = e;
        if (attempt < MAX_ATTEMPTS) {
          toast.message(`Network hiccup — retrying (${attempt}/${MAX_ATTEMPTS - 1})…`);
          await new Promise((r) => setTimeout(r, backoffs[attempt - 1] ?? 2000));
          continue;
        }
      }
    }
    setRetryAttempt(0);

    if (lastThrown) {
      setSubmitting(false);
      const msg = (lastThrown as Error)?.message || "Couldn't reach the server. Your info is saved — tap Retry.";
      setGenericError(msg);
      setShowErrorSummary(true);
      setRetryable(true);
      toast.error(msg);
      return;
    }

    const d = data as { ok?: boolean; error?: string; field?: string; message?: string; errors?: Record<string, string>; name?: string } | null;
    if (!d?.ok) {
      setSubmitting(false);
      // Field-level errors from backend
      if (d?.errors && typeof d.errors === "object") {
        setServerFieldErrors(d.errors);
        setShowErrorSummary(true);
        setGenericError(d.message || obt("Some fields need attention."));
        requestAnimationFrame(() => {
          const el = document.getElementById("onboard-error-summary");
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
          (el as HTMLElement | null)?.focus({ preventScroll: true });
        });
        toast.error(d.message || obt("Please fix the highlighted fields."));
        return;
      }
      if (d?.field === "images") {
        const msg = d.message || obt("There's a problem with your images.");
        setImageError(msg);
        setServerFieldErrors({ images: msg });
        setGenericError(msg);
        setShowErrorSummary(true);
        toast.error(msg);
        return;
      }
      const msg = d?.message || obt("Submit failed. Please try again.");
      setGenericError(msg);
      setShowErrorSummary(true);
      // Generic server-side failure with no field context — usually transient.
      setRetryable(true);
      toast.error(msg);
      return;
    }
    setImageError(null);
    setServerFieldErrors({});
    setShowErrorSummary(false);
    setRetryable(false);


    // Onboarding submitted — defer password creation to the success screen.
    setSubmitting(false);
    try { window.localStorage.removeItem(lsKey); window.localStorage.removeItem(lsImgKey); } catch { /* ignore */ }
    setPhase("done");
  }



  if (phase === "loading") return <Center><Loader2 className="h-5 w-5 animate-spin" /></Center>;
  if (phase === "invalid") return <Center><p>{obt("This onboarding link is invalid.")}</p></Center>;
  if (phase === "expired") return <Center><p>{obt("This onboarding link has expired. Please contact your account manager.")}</p></Center>;
  if (phase === "done") return (
    <Center>
      <div className="max-w-md w-full text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
        <h1 className="mt-4 text-2xl font-semibold">{obt("Awesome \u2014 you're in the queue!")}</h1>
        <p className="mt-2 text-slate-600">{obt("Our team will review your details and get you live shortly.")}</p>
        {images.length === 0 && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left">
            <div className="text-sm font-semibold text-amber-900">{obt("Add photos to go live faster")}</div>
            <p className="mt-1 text-xs text-amber-800">Your submission doesn't have any photos yet. We can't publish your listing without at least one. Reopen your onboarding to add them now — none of your info will be lost.</p>
            <button
              type="button"
              disabled={reopening}
              onClick={async () => {
                setReopening(true);
                try {
                  const res = await reopenOwn({ data: { token } }) as { ok: boolean; message?: string; already?: boolean };
                  if (!res.ok) { toast.error(res.message || obt("Couldn't reopen. Please try again.")); return; }
                  toast.success(obt("Reopened \u2014 add your photos and submit again."));
                  setPhase("form");
                } catch (e) {
                  toast.error((e as Error)?.message || obt("Couldn't reopen. Please try again."));
                } finally {
                  setReopening(false);
                }
              }}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-600 text-white px-4 py-2 text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
            >
              {reopening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Add Photos Now
            </button>
          </div>
        )}
        <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm border border-slate-100 text-left">
          {portalEmail ? (
            <>
              <div className="text-xs uppercase tracking-widest text-emerald-600 mb-1">{obt("Password Set")}</div>
              <p className="text-sm text-slate-700">{obt("Sign in anytime with ")}<strong>{portalEmail}</strong> to update photos, edit your listing, or check progress.</p>
              <Link to={"/partner/login" as never} className="mt-4 inline-flex items-center justify-center rounded-2xl bg-slate-900 text-white px-5 py-2.5 font-medium hover:bg-slate-800 w-full">{obt("Go To Partner Portal")}</Link>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{obt("Save Your Progress")}</div>
              <h2 className="text-lg font-semibold text-slate-900">{obt("Create a password to access your dashboard")}</h2>
              <p className="mt-1 text-sm text-slate-600">{obt("You'll use it to sign in later and manage your listing while we review it.")}</p>
              <div className="relative mt-4">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="password"
                  placeholder={obt("Choose a password (min. 8 characters)")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inpCls(passwordError) + " pl-9"}
                />
              </div>
              {passwordError && <p className="mt-1 text-xs text-red-600 text-left">{passwordError}</p>}
              <button
                disabled={password.length < 8 || submitting}
                onClick={async () => {
                  setSubmitting(true);
                  const act = await activate({ data: { token, password } });
                  setSubmitting(false);
                  if (!act.ok) { toast.error(obt("Couldn't create your portal account. Please try again.")); return; }
                  setPortalEmail(act.email ?? null);
                }}
                className="mt-4 w-full rounded-2xl bg-slate-900 text-white px-5 py-3 font-medium hover:bg-slate-800 disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Create Password & Continue
              </button>
              <button
                type="button"
                onClick={() => setPhase("done")}
                className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700"
              >
                Skip for now — I'll set it up from the email link later
              </button>
            </>
          )}
        </div>
      </div>
    </Center>
  );


  if (phase === "landing") return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg max-w-lg w-full p-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500"><Sparkles className="h-4 w-4" /> Welcome{clientName ? `, ${clientName}` : ""}</div>
        <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-slate-900">{obt("Let's Get You Listed")}</h1>
        <p className="mt-3 text-slate-600">Takes about 5 minutes. We'll ask for your address, opening hours, description, and a few photos. Your progress saves automatically — you can pause and finish later using this same link.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => setPhase("form")} className="flex-1 rounded-2xl bg-slate-900 text-white font-medium px-5 py-3 hover:bg-slate-800">{obt("Start Setup Now")}</button>
          <button
            onClick={async () => {
              const data = await deferOnboarding({ data: { token } });
              const d = data as { ok?: boolean; error?: string } | null;
              if (!d?.ok) {
                toast.error(obt("Couldn't schedule a reminder. Please try again."));
                return;
              }
              toast.success(obt("No problem \u2014 we'll email your setup link again soon."));
              setTimeout(() => window.close(), 900);
            }}
            className="flex-1 rounded-2xl border border-slate-200 text-slate-700 font-medium px-5 py-3 hover:bg-slate-100 inline-flex items-center justify-center gap-2"
          >
            <Clock className="h-4 w-4" /> Do It Later
          </button>
        </div>
      </div>
    </div>
  );



  // form
  return (
    <SaveCtx.Provider value={{ save: manualSave, saving: savingState === "saving" }}>
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-3 sm:px-4 pb-32 sm:pb-8">
      <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold truncate">{obt("Partner Onboarding")}</h1>
            <p className="text-xs sm:text-sm text-slate-500 truncate">{clientName}</p>
          </div>
          <div className="shrink-0 text-xs text-slate-500 flex items-center gap-2">
            <LangSwitchPill />
            {savingState === "saving" && <><Loader2 className="h-3 w-3 animate-spin" /> <span className="hidden sm:inline">{obt("Saving\u2026")}</span></>}
            {savingState === "saved" && <><CheckCircle2 className="h-3 w-3 text-emerald-500" /> <span className="hidden sm:inline">{obt("Saved")}</span></>}
            {savingState === "idle" && !saveError && dirty && <span className="hidden sm:inline">{obt("Unsaved changes")}</span>}
            <VersionHistoryDialog token={token} onRestore={applyRestoredVersion} />
          </div>

        </header>

        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          {obt("Your answers save automatically, and every section below has its own Save button. You can stop any time and continue later with the same link \u2014 nothing is lost.")}
        </div>

        {saveError && (
          <div role="alert" className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center gap-3">
            <span className="flex-1 min-w-[12rem]">{saveError}</span>
            <button
              type="button"
              onClick={() => { void manualSave(); }}
              className="rounded-full bg-amber-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
            >
              Save Now
            </button>
          </div>
        )}




        <div aria-live="assertive" aria-atomic="true" className="sr-only">
          {showErrorSummary && (isFormInvalid || Object.keys(serverFieldErrors).length > 0 || genericError)
            ? (genericError || obt("Form has errors. Please review the highlighted fields."))
            : ""}
        </div>
        {showErrorSummary && (isFormInvalid || Object.keys(serverFieldErrors).length > 0 || genericError) && (
          <div
            id="onboard-error-summary"
            role="alert"
            tabIndex={-1}
            aria-labelledby="onboard-error-summary-title"
            className="rounded-2xl border border-red-200 bg-red-50 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <div id="onboard-error-summary-title" className="text-sm font-semibold text-red-800">
              {genericError || obt("Please fix the following before submitting:")}
            </div>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-red-700">
              {fieldErrors.business_type && <li>Listing type: {fieldErrors.business_type}</li>}
              {fieldErrors.name && <li>Business name: {fieldErrors.name}</li>}
              {fieldErrors.address && <li>Address: {fieldErrors.address}</li>}
              {fieldErrors.city && <li>City: {fieldErrors.city}</li>}
              {fieldErrors.category && <li>Category: {fieldErrors.category}</li>}
              {fieldErrors.subcategory && <li>Subcategory: {fieldErrors.subcategory}</li>}
              {fieldErrors.description && <li>Description: {fieldErrors.description}</li>}
              {needsHours && hoursGeneric && <li>Opening hours: {hoursGeneric}</li>}
              {needsHours && DAYS.filter((d) => hoursErrors[d]).map((d) => <li key={d}>Hours ({d}): {hoursErrors[d]}</li>)}
              {needsHours && !hasAnyOpen && <li>{obt("At least one day must have opening hours.")}</li>}
              
              {serverFieldErrors.images && <li>Photos: {serverFieldErrors.images}</li>}
            </ul>
            {retryable && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => { void submit(); }}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Retrying{retryAttempt ? ` (${retryAttempt}/3)` : ""}…</> : obt("Retry Submit")}
                </button>
                <span className="text-xs text-red-700">{obt("Your entered information and photos are safe \u2014 nothing has been lost.")}</span>
              </div>
            )}
          </div>
        )}


        <Section title="What Are You Listing? *">
          <p className="text-sm text-slate-500 -mt-1 mb-2">{obt("Pick the option that best fits your business. We'll only ask for the fields that apply.")}</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {([
              { key: "restaurant", label: obt("Restaurant / Cafe / Bar"), blurb: obt("Food or drink venue with opening hours.") },
              { key: "place", label: obt("Place / Attraction / Shop"), blurb: "A physical venue guests can visit." },
              { key: "ticket", label: obt("Tickets / Event / Tour"), blurb: obt("Bookable experience or scheduled event.") },
            ] as const).map((opt) => {
              const active = draft.business_type === opt.key;
              return (
                <button
                  type="button"
                  key={opt.key}
                  onClick={() => setDraft({ ...draft, business_type: opt.key })}
                  className={`text-left rounded-2xl border p-3 transition ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}
                >
                  <div className={`text-sm font-semibold ${active ? "text-white" : "text-slate-900"}`}>{opt.label}</div>
                  <div className={`text-xs mt-1 ${active ? "text-slate-200" : "text-slate-500"}`}>{opt.blurb}</div>
                </button>
              );
            })}
          </div>
          {fieldErrors.business_type && <p className="text-xs text-red-600">{fieldErrors.business_type}</p>}
        </Section>




        <Section title="Business Details">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{obt("From Your Contract")}</div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              <div><dt className="text-xs text-slate-500">{obt("Business Name")}</dt><dd className="font-medium text-slate-900">{draft.name || "—"}</dd></div>
              {draft.website && <div className="sm:col-span-2"><dt className="text-xs text-slate-500">{obt("Website")}</dt><dd className="font-medium text-slate-900 break-all">{draft.website}</dd></div>}
            </dl>
            <p className="mt-3 text-xs text-slate-500">{obt("Need to change the business name or website? Reply to your onboarding email.")}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Address *" error={fieldErrors.address}>
              <input className={inpCls(fieldErrors.address)} value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} placeholder={obt("Street name and number")} />
            </Field>
            <Field label="City / Destination *" error={fieldErrors.city}>
              <CitySelect
                value={draft.city || ""}
                onChange={(v) => setDraft({ ...draft, city: v })}
                error={fieldErrors.city}
              />
            </Field>
            <Field label="Postal Code">
              <input
                className={inpCls(null)}
                value={draft.postal_code || ""}
                inputMode="numeric"
                onChange={(e) => {
                  const raw = e.target.value.replace(/\D/g, "").slice(0, 5);
                  setDraft({ ...draft, postal_code: raw });
                }}
                placeholder={obt("e.g. 00100")}
              />
              {(() => {
                const raw = (draft.postal_code || "").replace(/\D/g, "");
                if (raw.length !== 5) return null;
                const p2 = Number(raw.slice(0, 2));
                let auto: string | null = null;
                if (raw === "93600") auto = "Lapland";
                else if (p2 >= 94 && p2 <= 99) auto = "Lapland";
                else if (p2 === 10 || (p2 >= 20 && p2 <= 23) || (p2 >= 26 && p2 <= 29) || p2 === 48 || p2 === 49 || (p2 >= 65 && p2 <= 68) || (p2 >= 90 && p2 <= 93)) auto = obt("Archipelago");
                else if ([0, 1, 2, 6].includes(p2)) auto = obt("Coast of Finland");
                else auto = "Lakeland";
                return <p className="mt-1 text-[11px] text-slate-500">{obt("Region: ")}<span className="font-medium text-slate-700">{auto}</span> (auto-detected)</p>;
              })()}
            </Field>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <MapPin className="h-4 w-4 text-slate-500" /> Map pin (optional but recommended)
              </div>
              <button
                type="button"
                onClick={pickCurrentLocation}
                disabled={locating}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5" />}
                {locating ? "Locating…" : obt("Use My Current Location")}
              </button>
            </div>
            <p className="text-xs text-slate-500">{obt("Stand at your venue's entrance and tap the button, or paste coordinates from Google Maps.")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Latitude"><input className={inp} value={draft.lat != null ? String(draft.lat) : ""} onChange={(e) => setDraft({ ...draft, lat: e.target.value === "" ? null : Number(e.target.value) })} placeholder={obt("60.1699")} /></Field>
              <Field label="Longitude"><input className={inp} value={draft.lng != null ? String(draft.lng) : ""} onChange={(e) => setDraft({ ...draft, lng: e.target.value === "" ? null : Number(e.target.value) })} placeholder={obt("24.9384")} /></Field>
            </div>
            {draft.lat != null && draft.lng != null && (
              <a
                href={`https://www.google.com/maps?q=${draft.lat},${draft.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 underline"
              >
                Preview on Google Maps →
              </a>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Instagram"><input className={inp} value={draft.instagram || ""} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} placeholder={obt("@handle")} /></Field>
            <Field label="Facebook"><input className={inp} value={draft.facebook || ""} onChange={(e) => setDraft({ ...draft, facebook: e.target.value })} placeholder={obt("facebook.com/\u2026")} /></Field>
          </div>
        </Section>

        <Section title="Menu (Optional)">
          <p className="text-xs text-slate-500">
            Add your menu once and it shows on your What Finland page — a link, a PDF or a photo. Any one is enough.
          </p>
          <Field label="Link to your menu page">
            <input
              className={inp}
              value={draft.menu_external_url || ""}
              onChange={(e) => setDraft({ ...draft, menu_external_url: e.target.value })}
              placeholder={obt("https://\u2026/menu")}
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Menu PDF">
              <MenuFileInput
                accept="application/pdf"
                busy={menuUploading === "pdf"}
                value={draft.menu_pdf_url || ""}
                onClear={() => setDraft({ ...draft, menu_pdf_url: "" })}
                onFile={async (file) => {
                  setMenuUploading("pdf");
                  try {
                    const base64 = (await fileToDataUrl(file)).split(",")[1] ?? "";
                    const res: any = await uploadMenuFile({ data: { token, kind: "pdf", filename: file.name, base64 } });
                    if (res?.ok) { setDraft({ ...draft, menu_pdf_url: res.url }); toast.success(obt("Menu PDF uploaded")); }
                    else toast.error(res?.message || "Upload failed");
                  } catch { toast.error(obt("Upload failed")); }
                  finally { setMenuUploading(null); }
                }}
              />
            </Field>
            <Field label="Menu photo">
              <MenuFileInput
                accept="image/*"
                busy={menuUploading === "image"}
                value={draft.menu_image_url || ""}
                onClear={() => setDraft({ ...draft, menu_image_url: "" })}
                onFile={async (file) => {
                  setMenuUploading("image");
                  try {
                    const base64 = (await fileToDataUrl(file)).split(",")[1] ?? "";
                    const res: any = await uploadMenuFile({ data: { token, kind: "image", filename: file.name, base64 } });
                    if (res?.ok) { setDraft({ ...draft, menu_image_url: res.url }); toast.success(obt("Menu photo uploaded")); }
                    else toast.error(res?.message || "Upload failed");
                  } catch { toast.error(obt("Upload failed")); }
                  finally { setMenuUploading(null); }
                }}
              />
            </Field>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-900">{obt("Dishes (optional)")}</div>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  setDraft({ ...draft, menu_items: [...(draft.menu_items || []), { name: "" }] })
                }
              >
                {obt("Add dish")}
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{obt("Add dishes with prices and we build a real menu page for you.")}</p>
            <div className="mt-3 space-y-3">
              {(draft.menu_items || []).map((dish, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input
                      className={inp}
                      value={dish.name}
                      placeholder={obt("Dish name")}
                      onChange={(e) => {
                        const next = [...(draft.menu_items || [])];
                        next[i] = { ...dish, name: e.target.value };
                        setDraft({ ...draft, menu_items: next });
                      }}
                    />
                    <input
                      className={inp}
                      value={dish.section || ""}
                      placeholder={obt("Section (Starters, Mains\u2026)")}
                      onChange={(e) => {
                        const next = [...(draft.menu_items || [])];
                        next[i] = { ...dish, section: e.target.value };
                        setDraft({ ...draft, menu_items: next });
                      }}
                    />
                  </div>
                  <input
                    className={inp}
                    value={dish.description || ""}
                    placeholder={obt("Short description")}
                    onChange={(e) => {
                      const next = [...(draft.menu_items || [])];
                      next[i] = { ...dish, description: e.target.value };
                      setDraft({ ...draft, menu_items: next });
                    }}
                  />
                  <div className="grid sm:grid-cols-3 gap-2 items-center">
                    <input
                      className={inp}
                      value={dish.price || ""}
                      placeholder={obt("Price (e.g. 18.50)")}
                      onChange={(e) => {
                        const next = [...(draft.menu_items || [])];
                        next[i] = { ...dish, price: e.target.value };
                        setDraft({ ...draft, menu_items: next });
                      }}
                    />
                    <input
                      className={inp}
                      value={dish.diet || ""}
                      placeholder={obt("Diet (vegan, gluten-free\u2026)")}
                      onChange={(e) => {
                        const next = [...(draft.menu_items || [])];
                        next[i] = { ...dish, diet: e.target.value };
                        setDraft({ ...draft, menu_items: next });
                      }}
                    />
                    <button
                      type="button"
                      className="justify-self-start rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                      onClick={() => {
                        const next = [...(draft.menu_items || [])];
                        next.splice(i, 1);
                        setDraft({ ...draft, menu_items: next });
                      }}
                    >
                      {obt("Remove")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {capDiagnostic?.mismatch && (
          <div className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <div className="font-semibold">{obt("Heads up \u2014 your location limit may be outdated.")}</div>
            <div className="mt-1 text-amber-800">
              This form is showing <b>{capDiagnostic.rpc_cap}</b> location{capDiagnostic.rpc_cap === 1 ? "" : "s"}, but the latest signed contract for your account allows <b>{capDiagnostic.expected_cap}</b>.
              Please contact the what team to resync your account before adding extra venues.
            </div>
            <div className="mt-1 text-xs text-amber-700">
              Ref: contracts={capDiagnostic.contracts_seen}, contract_max={capDiagnostic.contract_max}
            </div>
          </div>
        )}
        <AdditionalLocationsSection
          locations={draft.locations || []}
          allowedLocations={allowedLocations}
          onChange={(next) => setDraft({ ...draft, locations: next })}
        />




        <Section title="About Your Place">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Category *" error={fieldErrors.category}>
              <SelectOrOther
                className={inpCls(fieldErrors.category)}
                value={draft.category || ""}
                onChange={(v) => setDraft({ ...draft, category: v })}
                options={categoryOptions}
                placeholder={obt("Select a category\u2026")}
              />
            </Field>
            <Field label="Subcategory *" error={fieldErrors.subcategory}>
              <SelectOrOther
                className={inpCls(fieldErrors.subcategory)}
                value={draft.subcategory || ""}
                onChange={(v) => setDraft({ ...draft, subcategory: v })}
                options={subcategoryOptions}
                placeholder={obt("Select a subcategory\u2026")}
              />
            </Field>
            <Field label="Vibe"><input className={inp} value={draft.vibe || ""} onChange={(e) => setDraft({ ...draft, vibe: e.target.value })} placeholder={obt("cozy, upscale, casual\u2026")} /></Field>
          </div>
          <Field label="Short description * — shown on your public listing page" error={fieldErrors.description}><textarea rows={3} className={inpCls(fieldErrors.description)} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
        </Section>


        <Section title="Finland-wide topics (optional)">
          <p className="text-sm text-slate-500 -mt-1 mb-3">
            Pick the topic pages your business belongs on. Your listing is shown on those pages too —
            it is never duplicated, it is the same listing referenced in more places.
          </p>
          <div className="flex flex-wrap gap-2">
            {HUB_OPTIONS.map((h) => {
              const selected = (draft.hubs || []).includes(h.slug);
              return (
                <button
                  key={h.slug}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    const current = draft.hubs || [];
                    setDraft({
                      ...draft,
                      hubs: selected ? current.filter((x) => x !== h.slug) : [...current, h.slug],
                    });
                  }}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                    selected
                      ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  }`}
                >
                  {h.label}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="More Details (Optional)">
          <p className="text-sm text-slate-500 -mt-1 mb-2">{obt("The more you tell us, the richer your listing looks. All fields below are optional.")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Neighbourhood / area"><input className={inp} value={draft.area || ""} onChange={(e) => setDraft({ ...draft, area: e.target.value })} placeholder={obt("e.g. Kallio, Punavuori")} /></Field>
            <Field label="Phone"><input className={inp} value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Public email"><input className={inp} value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
            {isTicket && (
              <Field label="Booking / ticket link"><input className={inp} value={draft.buy_ticket_url || ""} onChange={(e) => setDraft({ ...draft, buy_ticket_url: e.target.value })} placeholder={obt("https://\u2026")} /></Field>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Price range"><input className={inp} value={draft.price || ""} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder={obt("e.g. \u20ac\u20ac or 15\u201330 \u20ac")} /></Field>
            <Field label="Tags (comma separated)"><TagsInput className={inp} tags={draft.tags || []} onChange={(tags) => setDraft({ ...draft, tags })} /></Field>
          </div>
          <Field label="Overview (longer intro shown on your listing page)"><textarea rows={4} className={inp} value={draft.overview_body || ""} onChange={(e) => setDraft({ ...draft, overview_body: e.target.value })} /></Field>
          <Field label="Details (menu highlights, specialties, what makes you unique)"><textarea rows={4} className={inp} value={draft.details_body || ""} onChange={(e) => setDraft({ ...draft, details_body: e.target.value })} /></Field>
          {isTicket && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Schedule info"><textarea rows={3} className={inp} value={draft.schedule_info || ""} onChange={(e) => setDraft({ ...draft, schedule_info: e.target.value })} placeholder={obt("Recurring events, seasonal hours, etc.")} /></Field>
              <Field label="Tickets info"><textarea rows={3} className={inp} value={draft.tickets_info || ""} onChange={(e) => setDraft({ ...draft, tickets_info: e.target.value })} placeholder={obt("Booking rules, entry pricing, etc.")} /></Field>
            </div>
          )}
          <div>
            <Field label="Call-to-action label"><input className={inp} value={draft.cta || ""} onChange={(e) => setDraft({ ...draft, cta: e.target.value })} placeholder={obt("Reserve, Book, Get tickets\u2026")} /></Field>
          </div>
        </Section>



        {needsHours && (
        <Section title="Opening Hours *">
          <p className="text-sm text-slate-500 -mt-1 mb-2">
            Shown on your public listing page. Enter hours for every day, or tick{" "}
            <span className="font-medium">{obt("Closed")}</span>. You cannot submit until every day is set.
          </p>
          <div className="mb-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5 text-sm text-indigo-900">
            <div className="flex items-start gap-2">
              <Moon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
              <div>
                <p className="font-medium">{obt("Overnight closing times are allowed")}</p>
                <p className="text-indigo-800/80">
                  If you close after midnight, enter the time on the next day. For example{" "}
                  <span className="font-semibold">17:00 – 00:00</span> means open until midnight, and{" "}
                  <span className="font-semibold">22:00 – 02:00</span> means open until 2 am. We will
                  show a “next day” label so visitors understand.
                </p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {DAYS.map((d) => {
              const h = draft.hours?.[d] || emptyHours[d];
              const err = hoursErrors[d];
              const isOvernight = h.open && h.close && h.open !== h.close && h.close <= h.open;
              return (
                <div key={d} className="space-y-1 rounded-xl border border-slate-100 bg-slate-50/50 sm:border-0 sm:bg-transparent sm:rounded-none p-2 sm:p-0">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="w-10 sm:w-12 text-sm font-medium text-slate-700 shrink-0">{d}</span>
                    <label className="text-xs text-slate-500 flex items-center gap-1 shrink-0">
                      <input type="checkbox" checked={h.closed} onChange={(e) => setDraft({ ...draft, hours: { ...(draft.hours || emptyHours), [d]: { ...h, closed: e.target.checked } } })} />
                      Closed
                    </label>
                    {!h.closed && (
                      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end sm:justify-start">
                        <input type="time" className={`${inp} w-[7.5rem] sm:w-28 ${err ? "border-red-300 focus:ring-red-500/20" : ""}`} value={h.open} onChange={(e) => setDraft({ ...draft, hours: { ...(draft.hours || emptyHours), [d]: { ...h, open: e.target.value } } })} />
                        <span className="text-slate-400">–</span>
                        <input type="time" className={`${inp} w-[7.5rem] sm:w-28 ${err ? "border-red-300 focus:ring-red-500/20" : ""}`} value={h.close} onChange={(e) => setDraft({ ...draft, hours: { ...(draft.hours || emptyHours), [d]: { ...h, close: e.target.value } } })} />
                        {isOvernight && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 whitespace-nowrap rounded-full bg-indigo-100 px-2 py-0.5">
                            <Moon className="h-3 w-3" /> next day
                          </span>
                        )}
                      </div>

                    )}
                  </div>
                  {err && (
                    <p className="text-xs text-red-600 sm:ml-[3.75rem]">
                      {err === obt("Opening and closing times cannot be the same.") ? (
                        <>
                          {err} <span className="text-red-500">{obt("Overnight hours like 22:00\u201302:00 are fine \u2014 just make sure the times are different.")}</span>
                        </>
                      ) : (
                        err
                      )}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
        )}

        <Section title="Photos">
          <p className="text-sm text-slate-500 -mt-1 mb-3">JPG, PNG, WebP or AVIF. Up to {MAX_ONBOARDING_PHOTOS} photos — automatically compressed to under 250 KB. The first photo is your cover. Drag to reorder, or focus a tile and press ← / → (or Ctrl/Cmd + ← / →) to move it. Press Delete to remove.</p>
          {imageError && (
            <div role="alert" className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {imageError}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                aria-label={`Photo ${i + 1} of ${images.length}. Drag or use arrow keys to reorder.`}
                draggable
                onDragStart={(e) => {
                  setDragIndex(i);
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", String(i)); } catch {}
                }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIndex(i); }}
                onDragLeave={() => setDragOverIndex((v) => (v === i ? null : v))}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = dragIndex ?? Number(e.dataTransfer.getData("text/plain"));
                  if (!Number.isNaN(from) && from !== i) moveImage(from, i);
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); moveImage(i, i - 1); }
                  else if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); moveImage(i, i + 1); }
                  else if (e.key === "Home") { e.preventDefault(); moveImage(i, 0); }
                  else if (e.key === "End") { e.preventDefault(); moveImage(i, images.length - 1); }
                  else if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); removeImage(i); }
                }}
                className={`relative aspect-square rounded-2xl overflow-hidden border bg-slate-100 cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 ${dragOverIndex === i && dragIndex !== i ? "border-slate-900 ring-2 ring-slate-900" : "border-slate-200"} ${dragIndex === i ? "opacity-50" : ""}`}
              >
                <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover pointer-events-none" draggable={false} />
                {i === 0 && (
                  <span className="absolute top-1 left-1 inline-flex items-center gap-1 rounded-full bg-slate-900/85 text-white text-[10px] font-medium px-2 py-0.5">
                    <Star className="h-2.5 w-2.5" /> Cover
                  </span>
                )}
                <button
                  type="button"
                  aria-label="Remove photo"
                  onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="absolute top-1 right-1 bg-white/95 hover:bg-white rounded-full p-1 shadow-sm border border-slate-200"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <span className="absolute bottom-1 left-1 text-[10px] font-medium text-white px-1.5 py-0.5 rounded-full bg-black/50">{i + 1}</span>
              </div>
            ))}
            {images.length < MAX_ONBOARDING_PHOTOS && (
              <label className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:bg-slate-100 cursor-pointer text-sm">
                <Upload className="h-5 w-5 mb-1" /> Add photo
                <input ref={fileRef} type="file" multiple accept={IMAGE_ACCEPT_ATTR} className="hidden" onChange={(e) => handleFiles(e.target.files)} />
              </label>
            )}
          </div>
          {images.length > 0 && (
            <p className="mt-2 text-xs text-slate-500">{images.length} of {MAX_ONBOARDING_PHOTOS} photo{images.length === 1 ? "" : "s"} added.</p>
          )}
          {uploading > 0 && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Compressing {uploadingCount} image{uploadingCount > 1 ? "s" : ""}…</span><span>{uploading}%</span></div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-slate-900 transition-all" style={{ width: `${uploading}%` }} /></div>
            </div>
          )}
          {sizeNotes.length > 0 && uploading === 0 && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600 space-y-1">
              <div className="font-medium text-slate-700">{obt("Auto-resized to fit under 250 KB (max 1920 px):")}</div>
              {sizeNotes.map((n, i) => {
                const kb = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${Math.round(b / 1024)} KB`);
                const pct = n.before > 0 ? Math.round((1 - n.after / n.before) * 100) : 0;
                return (
                  <div key={i} className="flex justify-between gap-2">
                    <span className="truncate">{n.name}</span>
                    <span className="tabular-nums text-slate-500 shrink-0">{kb(n.before)} → <span className="text-emerald-700 font-medium">{kb(n.after)}</span>{pct > 0 && ` (-${pct}%)`}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>


        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:static sm:inset-auto sm:border-0 sm:bg-transparent sm:backdrop-blur-0 sm:p-0 sm:sticky sm:bottom-4">
          <div className="max-w-3xl mx-auto flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button onClick={manualSave} className="flex-1 rounded-2xl border border-slate-300 bg-white px-5 py-3 font-medium text-slate-800 hover:bg-slate-100">{obt("Save Draft")}</button>
            <button
              onClick={submit}
              disabled={submitting || uploading > 0 || isFormInvalid || images.length < 1}
              title={uploading > 0 ? "Please wait for image uploads to finish" : images.length < 1 ? "Add at least 1 photo to submit" : isFormInvalid ? "Fill in all required fields to continue" : undefined}
              className="flex-1 rounded-2xl bg-slate-900 text-white px-5 py-3 font-medium hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {(submitting || uploading > 0) && <Loader2 className="h-4 w-4 animate-spin" />}
              {uploading > 0 ? `${tx("Compressing")}… ${uploading}%` : tx("Submit Onboarding")}
            </button>

          </div>
        </div>

      </div>
    </div>
    </SaveCtx.Provider>
  );
}

const SaveCtx = createContext<{ save: () => void | Promise<void>; saving: boolean } | null>(null);

function SectionSaveButton() {
  const obt = useObT();
  const ctx = useContext(SaveCtx);
  if (!ctx) return null;
  return (
    <button
      type="button"
      onClick={() => { void ctx.save(); }}
      disabled={ctx.saving}
      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
    >
      {ctx.saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
      Save
    </button>
  );
}


const inp = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10";
const inpErr = "w-full rounded-xl border border-red-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500/20";
function inpCls(err?: string | null) { return err ? inpErr : inp; }

function Field({ label, error, children }: { label: string; error?: string | null; children: React.ReactNode }) {
  const obt = useObT();
  const fi = obt(label);
  const auto = useAutoTranslate(label);
  const localized = fi !== label ? fi : auto;
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 mb-1 block">{localized}</span>
      {children}
      {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
    </label>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const obt = useObT();
  const fi = obt(title);
  const auto = useAutoTranslate(title);
  const localized = fi !== title ? fi : auto;
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">{localized}</h2>
        <SectionSaveButton />
      </div>
      {children}
    </section>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 text-slate-700">{children}</div>;
}

// City picker sourced from the admin-managed cities list. Live listings only
// publish under a city that exists in Admin, so the input is a locked select.
function CitySelect({
  value,
  onChange,
  error,
  placeholder = "— Select A City —",
  allowEmpty = false,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  placeholder?: string;
  allowEmpty?: boolean;
}) {
  const obt = useObT();
  const cities = usePublicCities();
  const cls = error ? inpErr : inp;
  const norm = (value || "").trim();
  const lower = norm.toLowerCase();
  // Match by slug OR by name (partners may have typed "Helsinki" earlier).
  const matched =
    cities.find((c) => c.slug.toLowerCase() === lower) ||
    cities.find((c) => c.name.toLowerCase() === lower);
  const selectValue = matched?.name ?? (norm && !cities.length ? norm : "");
  const showLegacyOption = !!norm && !matched && cities.length > 0;
  return (
    <>
      <select
        className={cls}
        value={selectValue}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{allowEmpty ? placeholder : `${placeholder}`}</option>
        {cities.map((c) => (
          <option key={c.slug} value={c.name}>{c.name}</option>
        ))}
        {showLegacyOption && <option value={norm}>{norm} (unlisted)</option>}
      </select>
      {showLegacyOption && (
        <p className="mt-1 text-[11px] text-amber-700">
          "{norm}" is not in the admin city list — pick an available city so your listing can go live.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Additional locations UI — partners with more than one venue can add N extras.
// The primary venue lives on the top-level draft fields; entries here become
// additional places rows + crm_client_locations on approve.
// ---------------------------------------------------------------------------
function AdditionalLocationsSection({
  locations,
  allowedLocations,
  onChange,
}: {
  locations: LocationEntry[];
  allowedLocations: number;
  onChange: (next: LocationEntry[]) => void;
}) {
  const obt = useObT();
  const primaryCount = 1;
  const used = primaryCount + locations.length;
  const remaining = Math.max(0, allowedLocations - used);
  const atCap = used >= allowedLocations;

  function update(id: string, patch: Partial<LocationEntry>) {
    onChange(locations.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function remove(id: string) {
    onChange(locations.filter((l) => l.id !== id));
  }
  function add() {
    if (atCap) {
      toast.error(`Your agreement includes ${allowedLocations} location${allowedLocations === 1 ? "" : "s"}. Contact the what team to add more.`);
      return;
    }
    onChange([...(locations || []), emptyLocation()]);
  }

  return (
    <Section title="Additional Locations">
      <div
        className={`-mt-1 mb-3 rounded-2xl border px-4 py-3 ${
          allowedLocations > 1
            ? "border-emerald-200 bg-emerald-50"
            : "border-slate-200 bg-slate-50"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-sm font-semibold ${
                allowedLocations > 1
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-700 text-white"
              }`}
            >
              {allowedLocations}
            </span>
            <div className="text-sm font-semibold text-slate-900">
              Your agreement includes {allowedLocations} location{allowedLocations === 1 ? "" : "s"}
            </div>
          </div>
          <div className="text-xs font-medium text-slate-600">
            {used} of {allowedLocations} added
            {remaining > 0 && ` · ${remaining} remaining`}
          </div>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/70">
          <div
            className={`h-full transition-all ${allowedLocations > 1 ? "bg-emerald-600" : "bg-slate-800"}`}
            style={{ width: `${Math.min(100, (used / Math.max(1, allowedLocations)) * 100)}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-slate-600">
          Your primary venue above counts as location 1. Add each additional venue below — they'll all appear on the public map and CRM map.
        </p>
      </div>

      {locations.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-sm text-slate-500">
          {allowedLocations > 1
            ? `No extra locations yet — tap "Add Another Location" below to add up to ${allowedLocations - 1} more.`
            : "Your current agreement covers one venue. Contact the what team if you need to add more."}
        </div>
      )}

      <div className="space-y-4">
        {locations.map((loc, idx) => (
          <LocationCard
            key={loc.id}
            index={idx}
            loc={loc}
            onChange={(patch) => update(loc.id, patch)}
            onRemove={() => remove(loc.id)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={add}
        disabled={atCap}
        title={atCap ? "Contact the what team to add more locations" : undefined}
        className={`mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
          atCap
            ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
            : "border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700"
        }`}
      >
        + Add Another Location{!atCap && remaining > 0 ? ` (${remaining} left)` : ""}
      </button>
      {atCap && (
        <p className="mt-2 text-xs text-slate-500">
          Reached your contracted allowance. Contact the what team if you need additional locations.
        </p>
      )}

    </Section>
  );
}

function LocationCard({
  index,
  loc,
  onChange,
  onRemove,
}: {
  index: number;
  loc: LocationEntry;
  onChange: (patch: Partial<LocationEntry>) => void;
  onRemove: () => void;
}) {
  const obt = useObT();
  const [locating, setLocating] = useState(false);
  const [showHours, setShowHours] = useState(!!loc.hours);
  const [imgErr, setImgErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(0);

  function pickHere() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      toast.error(obt("Your browser doesn't support location services."));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
        });
        setLocating(false);
        toast.success(obt("Location captured."));
      },
      () => {
        setLocating(false);
        toast.error(obt("Couldn't read your location. Enter coordinates manually."));
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
    );
  }

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/avif"];
    const arr = Array.from(files);
    setImgErr(null);
    setUploading(1);
    const out: StoredImage[] = [];
    for (let i = 0; i < arr.length; i++) {
      const f = arr[i];
      if (!ALLOWED.includes(f.type.toLowerCase())) {
        setImgErr(`${f.name}: unsupported file type.`);
        setUploading(Math.round(((i + 1) / arr.length) * 100));
        continue;
      }
      try {
        const target = targetBytesFor(f);
        const compressed = await compressImage(f, target);
        if (compressed.size > target) throw new Error(`still larger than ${Math.round(target / 1024)} KB after compression`);
        const dataUrl = await fileToDataUrl(compressed);
        out.push({ name: f.name, dataUrl, size: compressed.size });
      } catch (e) {
        setImgErr(`${f.name}: ${(e as Error).message}`);
      }
      setUploading(Math.round(((i + 1) / arr.length) * 100));
    }
    onChange({ images: [...(loc.images || []), ...out] });
    setUploading(0);
  }

  function removeImage(i: number) {
    onChange({ images: (loc.images || []).filter((_, ix) => ix !== i) });
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">Location #{index + 2}</div>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
        >
          <X className="h-3.5 w-3.5" /> Remove
        </button>
      </div>

      <Field label="Location name / label">
        <input
          className={inp}
          value={loc.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder={obt("e.g. Kallio branch, Airport kiosk, Summer terrace")}
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Address *">
          <input
            className={inp}
            value={loc.address}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder={obt("Street name and number")}
          />
        </Field>
        <Field label="City / Destination">
          <CitySelect
            value={loc.city}
            onChange={(v: string) => onChange({ city: v })}
            placeholder={obt("\u2014 Use primary city \u2014")}
            allowEmpty
          />
        </Field>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <MapPin className="h-4 w-4 text-slate-500" /> Map pin
          </div>
          <button
            type="button"
            onClick={pickHere}
            disabled={locating}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5" />}
            {locating ? "Locating…" : "Use Current Location"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input
            className={inp}
            value={loc.lat != null ? String(loc.lat) : ""}
            onChange={(e) => onChange({ lat: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder={obt("Latitude (e.g. 60.1699)")}
          />
          <input
            className={inp}
            value={loc.lng != null ? String(loc.lng) : ""}
            onChange={(e) => onChange({ lng: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder={obt("Longitude (e.g. 24.9384)")}
          />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" /> Opening hours
          </div>
          <label className="text-xs text-slate-600 inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={showHours}
              onChange={(e) => {
                setShowHours(e.target.checked);
                onChange({ hours: e.target.checked ? { ...emptyHours } : null });
              }}
            />
            Different from primary
          </label>
        </div>
        {!showHours ? (
          <p className="text-xs text-slate-500">{obt("Inherits the primary opening hours from above.")}</p>
        ) : (
          <div className="space-y-1.5">
            <p className="text-xs text-indigo-700 bg-indigo-50 rounded-lg px-2 py-1.5">
              <Moon className="h-3 w-3 inline-block mr-1" />
              Overnight hours are allowed — e.g. 22:00–02:00 closes at 2 am next day.
            </p>
            {DAYS.map((d) => {
              const h = loc.hours?.[d] || emptyHours[d];
              const isOvernight = h.open && h.close && h.open !== h.close && h.close <= h.open;
              return (
                <div key={d} className="flex items-center gap-2 flex-wrap">
                  <span className="w-10 text-xs font-medium text-slate-600">{d}</span>
                  <label className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={h.closed}
                      onChange={(e) =>
                        onChange({
                          hours: { ...(loc.hours || emptyHours), [d]: { ...h, closed: e.target.checked } },
                        })
                      }
                    />
                    Closed
                  </label>
                  {!h.closed && (
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                      <input
                        type="time"
                        className={`${inp} w-[7rem]`}
                        value={h.open}
                        onChange={(e) =>
                          onChange({
                            hours: { ...(loc.hours || emptyHours), [d]: { ...h, open: e.target.value } },
                          })
                        }
                      />
                      <span className="text-slate-400">–</span>
                      <input
                        type="time"
                        className={`${inp} w-[7rem]`}
                        value={h.close}
                        onChange={(e) =>
                          onChange({
                            hours: { ...(loc.hours || emptyHours), [d]: { ...h, close: e.target.value } },
                          })
                        }
                      />
                      {isOvernight && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 whitespace-nowrap rounded-full bg-indigo-100 px-2 py-0.5">
                          <Moon className="h-3 w-3" /> next day
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <div className="text-sm font-medium text-slate-700">{obt("Photos for this location (optional)")}</div>
        <p className="text-xs text-slate-500">{obt("Falls back to your main gallery if left empty.")}</p>
        {imgErr && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700">{imgErr}</div>
        )}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {(loc.images || []).map((img, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
              <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 rounded-full bg-white/95 p-0.5 shadow-sm border border-slate-200"
                aria-label="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:bg-slate-100 cursor-pointer text-xs">
            <Upload className="h-4 w-4 mb-0.5" /> Add
            <input type="file" multiple accept={IMAGE_ACCEPT_ATTR} className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          </label>
        </div>
        {uploading > 0 && (
          <div className="text-xs text-slate-500">Compressing… {uploading}%</div>
        )}
      </div>
    </div>
  );
}



/** Small upload control for the onboarding menu PDF / photo. */
function MenuFileInput({
  accept, busy, value, onFile, onClear,
}: {
  accept: string;
  busy: boolean;
  value: string;
  onFile: (file: File) => void | Promise<void>;
  onClear: () => void;
}) {
  const obt = useObT();
  return (
    <div className="space-y-1.5">
      <input
        type="file"
        accept={accept}
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void onFile(f);
        }}
        className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white disabled:opacity-60"
      />
      {busy && <p className="text-xs text-slate-500">{obt("Uploading\u2026")}</p>}
      {value && !busy && (
        <div className="flex items-center gap-2 text-xs">
          <a href={value} target="_blank" rel="noopener noreferrer" className="underline text-slate-700 truncate">
            Uploaded — view
          </a>
          <button type="button" onClick={onClear} className="text-red-600 hover:underline">{obt("Remove")}</button>
        </div>
      )}
    </div>
  );
}
